import { useState, useEffect } from 'react';

interface VibeScoreRatingProps {
  score: number | null;
  notes: string | null;
  updatedAt: string | null;
  onUpdate: (score: number, notes: string) => Promise<void>;
}

export default function VibeScoreRating({ score, notes, updatedAt, onUpdate }: VibeScoreRatingProps) {
  const [selectedScore, setSelectedScore] = useState<number | null>(score);
  const [localNotes, setLocalNotes] = useState(notes || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    setSelectedScore(score);
    setLocalNotes(notes || '');
  }, [score, notes]);

  const handleScoreClick = async (newScore: number) => {
    setSelectedScore(newScore);
    setIsUpdating(true);
    try {
      await onUpdate(newScore, localNotes);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNotesBlur = async () => {
    if (selectedScore && localNotes !== notes) {
      setIsUpdating(true);
      try {
        await onUpdate(selectedScore, localNotes);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const getScoreColor = (scoreValue: number) => {
    if (scoreValue <= 3) return 'from-red-600 to-red-500';
    if (scoreValue <= 6) return 'from-yellow-600 to-yellow-500';
    return 'from-green-600 to-green-500';
  };

  return (
    <div className="bg-gradient-to-br from-yellow-900/20 to-black border border-yellow-400/30 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-yellow-400 font-bold text-lg flex items-center gap-2">
            Interview Vibe Score
            {isUpdating && <span className="text-xs text-gray-400 animate-pulse">Saving...</span>}
          </h3>
          <p className="text-gray-400 text-sm">Rate the interview from 1 (not a fit) to 10 (perfect fit)</p>
        </div>
        {updatedAt && (
          <div className="text-xs text-gray-500">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-red-400 text-xs font-medium whitespace-nowrap">Not a Fit</span>
        <div className="flex gap-1 flex-1 justify-center">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((scoreValue) => (
            <button
              key={scoreValue}
              onClick={() => handleScoreClick(scoreValue)}
              disabled={isUpdating}
              className={`
                w-10 h-10 rounded-lg font-bold text-sm transition-all duration-200
                ${selectedScore === scoreValue
                  ? `bg-gradient-to-br ${getScoreColor(scoreValue)} text-white scale-110 shadow-lg ring-2 ring-yellow-400`
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }
                ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {scoreValue}
            </button>
          ))}
        </div>
        <span className="text-green-400 text-xs font-medium whitespace-nowrap">Perfect Fit</span>
      </div>

      {selectedScore && (
        <div className="text-center mb-3">
          <span className={`inline-block px-4 py-2 rounded-full font-bold bg-gradient-to-r ${getScoreColor(selectedScore)} text-white`}>
            Current Score: {selectedScore}/10
          </span>
        </div>
      )}

      <button
        onClick={() => setShowNotes(!showNotes)}
        className="text-gray-400 hover:text-yellow-400 text-sm font-medium mb-2 transition-colors"
      >
        {showNotes ? '− Hide Notes' : '+ Add Notes'}
      </button>

      {showNotes && (
        <textarea
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          onBlur={handleNotesBlur}
          placeholder="Add notes about the interview vibe, communication style, red flags, or positive impressions..."
          className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition-colors resize-none"
          rows={3}
          disabled={isUpdating}
        />
      )}
    </div>
  );
}
